<template>
  <div class="group-administration-container">
    <p class="page-layout-padding mb-0" data-layout="group-administration">
      {{ $getText('GROUP_ADMINISTRATION_DESCRIPTION') }}<br />
      {{ $getText('GROUP_ADMINISTRATION_DESCRIPTION_2') }}
    </p>
    <reported-content v-if="group.id == $route.params.groupId" :group="group" />
    <div class="group-applications page-layout-padding">
      <template v-if="applicationItems.length > 0">
        <div v-if="!isMobile" class="application-actions">
          <b-btn size="sm" variant="link" @click="updateMultipleMemberships(groupMembershipRoles.MEMBER)">
            {{ $getText('GROUP_ADMINISTRATION_APPROVE_APPLICATION') }}
            {{ $getText('GROUP_ADMINISTRATION_CHOSEN_APPLICATION') }}
          </b-btn>
          <b-btn size="sm" variant="link" @click="updateMultipleMemberships(groupMembershipRoles.APPLICATIONREMOVED)">
            {{ $getText('GROUP_ADMINISTRATION_REJECT_APPLICATION') }}
            {{ $getText('GROUP_ADMINISTRATION_CHOSEN_APPLICATION') }}
          </b-btn>
        </div>
        <h3>{{ $getText('GROUP_ADMINISTRATION_APPLICATIONS') }}:</h3>
        <aula-table
          :items="applicationItems"
          :fields="fields"
          table-ref="groupApplicationsTable"
          :responsive="isMobile"
          :parent="parentTypes.PORTAL"
          @updateMembership="updateMembership"
          @applicationCheckboxClicked="applicationCheckboxClicked"
          @toggleAllCheckboxes="toggleAllCheckboxes"
        />
        <b-alert
          variant="warning"
          :show="dismissCountDown"
          dismissible
          @dismissed="dismissCountDown = 0"
          @dismiss-count-down="countDownChanged"
        >
          {{ $getText('GROUP_ADMINISTRATION_NOT_ADDDED') }}
        </b-alert>
        <div class="clearfix" />
      </template>
      <template v-if="groupMembers.length > 0">
        <h3>{{ $getText('GROUP_ADMINISTRATION_MEMBER') }}:</h3>
        <aula-table
          :items="groupMembers"
          :striped="false"
          :total-rows="groupMembers.length"
          :per-page="20"
          :fields="memberFields"
          :parent="parentTypes.PORTAL"
          :responsive="isMobile"
          table-ref="groupMembersTable"
          :show-pagination="true"
          @updateMembership="updateMembership"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { types } from '../../store/types/types';
import { mapActions, mapGetters } from 'vuex';
import ReportedContent from '../../../shared/components/ReportedContent';
import AulaTable from '../../../shared/components/Table';
import Vue from 'vue';
import { permissionEnum } from '../../../shared/enums/permissionEnum.ts';
import { groupTypes } from '../../../shared/enums/groupTypes';
import { parentTypes } from '../../../shared/enums/parentTypes.ts';
import { groupMembershipRoles } from '../../../shared/enums/groupMembershipRoles';
import { groupAccessTypes } from '../../../shared/enums/groupAccessTypes';
import { useUserProfileStore } from '../../../shared/stores';

export default {
  data: function () {
    return {
      parentTypes: parentTypes,
      permissionEnum: permissionEnum,
      groupMembershipRoles: groupMembershipRoles,
      selectedApplications: [],
      dismissCountDown: 0,
    };
  },
  computed: {
    ...mapGetters({
      group: types.GET_ACTIVE_GROUP,
      institutions: types.GET_INSTITUTIONS,
      groupMemberships: types.GET_GROUP_MEMBERSHIPS,
      applicationWasRemoved: types.GET_APPLICATION_WAS_REMOVED,
      hasPermission: types.HAS_PERMISSION,
      hasPermissionOnGroup: types.HAS_PERMISSION_ON_GROUP,
      isMobile: types.GET_IS_MOBILE,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    fields() {
      const fields = {
        profile: { label: 'Bruger', sortable: true },
        actions: { label: 'Handling', type: 'action' },
        checkboxes: {
          label: this.$getText('TABLE_SELECT_ALL_FILTERS'),
          type: 'checkbox',
          showSelectAllInHeader: true,
          allSelected: false,
          selectedArrays: {
            applications: {
              selected: [],
            },
          },
        },
      };
      if (!this.hasPermissionOnGroup(permissionEnum.HANDLE_GROUP_APPLICATION, this.group.id)) {
        delete fields.actions;
        delete fields.checkboxes;
      }
      return fields;
    },
    memberFields() {
      const memberFields = {
        profile: {
          label: this.$getText('GROUP_ADMINISTRATION_MEMBER_NAME'),
          sortable: true,
        },
        institutionRole: {
          label: this.$getText('GROUP_ADMINISTRATION_MEMBER_ROLE'),
          sortable: true,
        },
        actions: {
          label: this.$getText('GROUP_ADMINISTRATION_MEMBER_ACTION'),
          type: 'action',
        },
      };
      if (
        !this.hasPermissionOnGroup(permissionEnum.HANDLE_GROUP_APPLICATION, this.group.id) ||
        this.group.access.toLowerCase() != groupAccessTypes.APPLICATION ||
        (this.group.type.toLowerCase() != groupTypes.INSTITUTIONAL &&
          this.group.type.toLowerCase() != groupTypes.MUNICIPAL)
      ) {
        delete memberFields.actions;
      }
      return memberFields;
    },
    applicationItems() {
      const itemsArray = [];
      for (const obj of this.groupMemberships) {
        if (obj.groupRole == groupMembershipRoles.APPLIED) {
          const item = {
            profile: this.$getProfileDisplayName(obj.institutionProfile),
            actions: [
              {
                variant: 'link',
                method: 'updateMembership',
                textkey: 'GROUP_ADMINISTRATION_APPROVE_APPLICATION',
                parameters: { groupRole: groupMembershipRoles.MEMBER },
              },
              {
                variant: 'link',
                method: 'updateMembership',
                textkey: 'GROUP_ADMINISTRATION_REJECT_APPLICATION',
                parameters: {
                  groupRole: groupMembershipRoles.APPLICATIONREMOVED,
                },
              },
            ],
            checkboxes: {
              method: 'applicationCheckboxClicked',
              checkboxType: 'applications',
            },
            itemId: obj,
          };
          itemsArray.push(item);
        }
      }
      return itemsArray;
    },
    groupMembers() {
      const itemsArray = [];
      for (const member of this.groupMemberships) {
        if (
          member.groupRole !== groupMembershipRoles.MEMBER ||
          this.institutions.some(inst => inst.institutionProfileId == member.institutionProfile?.id)
        ) {
          continue;
        }
        const isOtp = member.otpInboxId != null;
        const memberProfile = isOtp
          ? member.otpInboxMembershipDisplayName
          : this.$getProfileDisplayName(member.institutionProfile);
        const memberInstitutionRole = isOtp
          ? this.$getText('INSTITUTION_ROLE_OTP')
          : this.$getText('INSTITUTION_ROLE_' + member.institutionRole.split('-').join('').toUpperCase());

        itemsArray.push({
          profile: memberProfile,
          institutionRole: memberInstitutionRole,
          actions: isOtp
            ? []
            : [
                {
                  variant: 'link',
                  method: 'updateMembership',
                  textkey: 'GROUP_ADMINISTRATION_REMOVE_MEMBER',
                  parameters: { groupRole: groupMembershipRoles.INACTIVE },
                },
              ],
          itemId: member,
        });
      }
      return itemsArray;
    },
  },
  methods: {
    ...mapActions({
      setMembership: types.SET_MEMBERSHIP,
      setApplicationWasRemoved: types.SET_APPLICATION_WAS_REMOVED,
    }),
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    toggleAllCheckboxes(allSelected) {
      if (allSelected) {
        this.selectedApplications = this.applicationItems.map(item => item.itemId);
        this.fields.checkboxes.allSelected = true;
        this.fields.checkboxes.selectedArrays.applications.selected = this.applicationItems.map(item => item.itemId);
      } else {
        this.selectedApplications = [];
        this.fields.checkboxes.allSelected = false;
        this.fields.checkboxes.selectedArrays.applications.selected = [];
      }
    },
    updateMembership(response) {
      const em = this;
      const memberId =
        response.item.itemId.institutionProfile != null ? response.item.itemId.institutionProfile.id : null;
      this.setMembership({
        groupRole: response.parameters.groupRole,
        groupId: em.group.id,
        memberId: memberId,
      })
        .then(function () {
          em.updateApplicationsTable(memberId, response.parameters.groupRole);
        })
        .catch(function (error) {
          if (error.response.data.errorMessage.includes('Application rem')) {
            em.dismissCountDown = 5;
            em.updateApplicationsTable(response.item.itemId, groupMembershipRoles.APPLICATIONREMOVED);
          }
        });
    },
    updateMultipleMemberships(groupRole) {
      for (const member of this.selectedApplications) {
        const memberId = member.institutionProfile != null ? member.institutionProfile.id : member.memberGroup.id;
        this.setMembership({
          groupRole: groupRole,
          groupId: this.group.id,
          memberId: memberId,
          id: member.id,
        })
          .then(() => {
            this.updateApplicationsTable(memberId, groupRole);
          })
          .catch(error => {
            if (error.response.data.errorMessage.includes('Application rem')) {
              this.dismissCountDown = 5;
              this.updateApplicationsTable(memberId, groupMembershipRoles.APPLICATIONREMOVED);
            }
          });
      }
      this.selectedApplications = [];
      this.fields.checkboxes.selectedArrays.applications.selected = [];
    },
    updateApplicationsTable(memberId, groupRole) {
      this.groupMemberships.forEach(function (membership) {
        if (membership.institutionProfile != null && membership.institutionProfile.id === memberId) {
          membership.groupRole = groupRole;
        }
      });
    },
    applicationCheckboxClicked(fields) {
      Vue.nextTick(() => {
        this.selectedApplications = fields.checkboxes.selectedArrays.applications.selected;
        this.fields.checkboxes.allSelected = this.selectedApplications.length === this.applicationItems.length;
      });
    },
  },
  components: {
    AulaTable,
    ReportedContent,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.group-search {
  position: absolute;
  top: 0px;
  right: 0px;
}

.application-actions {
  float: right;
  position: relative;

  .btn-link {
    text-decoration: underline;
  }
}
</style>
